/**
 * Assessments management page.
 * List with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AssessmentsDataTable } from '@/components/assessments/AssessmentsDataTable';
import { CreateAssessmentView } from '@/components/assessments/CreateAssessmentView';
import { EditAssessmentView } from '@/components/assessments/EditAssessmentView';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { Button } from '@/components/forms/Button';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Assessments list with CollapseReplace create/edit. */
export const AssessmentsManagementPage: React.FC = () => {
  const { canCreate } = usePageActions('assessments');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editAssessmentId, setEditAssessmentId] = React.useState<string | null>(null);
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode } = useViewMode('assessments-management');
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** Deep-link: open create/view via `?create=` / `?view=`, then strip the params. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (viewId) {
      setOpenRow(viewId);
      setActiveForm(null);
      setEditAssessmentId(null);
    }

    if (shouldCreate && canCreate) {
      setActiveForm('create');
      setEditAssessmentId(null);
      closeRow();
    }

    if (viewId || shouldCreate) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          next.delete('create');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, canCreate, closeRow, setOpenRow, setSearchParams]);

  /** Invalidate assessment queries then close (also used as create onSuccess; await so the table sees new data). */
  const closeForm = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['assessments'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['assessment'], refetchType: 'all' }),
    ]);
    setActiveForm(null);
    setEditAssessmentId(null);
  }, [queryClient]);

  /** Swap the table for the edit form and close any open details row. */
  const handleEditAssessment = React.useCallback(
    (assessmentId: string) => {
      setEditAssessmentId(assessmentId);
      setActiveForm('edit');
      closeRow();
    },
    [closeRow],
  );

  /** Scroll the page to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  /** Pick the collapse-wrapper form from `activeForm`. */
  const formContent = React.useMemo(() => {
    if (activeForm === 'create') {
      return <CreateAssessmentView onClose={closeForm} onSuccess={closeForm} />;
    }
    if (activeForm === 'edit' && editAssessmentId) {
      return (
        <EditAssessmentView
          key={editAssessmentId}
          assessmentId={editAssessmentId}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editAssessmentId, closeForm]);

  return (
    <ManagementPage
      title="Gestion des Bilans"
      subtitle="Générez, complétez et validez les bilans des volontaires et intervenants"
      contentRef={contentRef}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setActiveForm('create');
              setEditAssessmentId(null);
              closeRow();
            }}
          >
            Générer un bilan
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <AssessmentsDataTable
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditAssessment={handleEditAssessment}
          enableFilters
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
};
